import {
  describeErrorName,
  recordSerializerFallback,
} from '@/components/tiptap/shared/serializer-fallback';
import {
  isTableColwidthsNamespaceLine,
  parseTableColwidthsMarker,
  stripTableColwidthsMarkerLines,
  TABLE_COLWIDTHS_NAMESPACE_SOURCE,
} from '@/components/tiptap/shared/table-colwidths-marker';
import { isScribeBlockEditorCodeblockEnabled } from '@/features/flags';
import { isCopyExternalPasteStylesEnabled } from '@/features/flags/copy-external-paste-styles-flag';
import { isTableColwidthPersistenceEnabled } from '@/features/flags/table-colwidth-persistence-flag';
import { isTableRoundTripFixKilled } from '@/features/flags/table-roundtrip-flag';
import { isTableRowRejoinKilled } from '@/features/flags/table-row-rejoin-flag';
import { collapseSameTypeListItemGaps } from '@/helpers/listSpacing';
import { wrapHtmlInSections } from '@/helpers/sectionParser';
import { logger } from '@/utils/logger/logger';
import {
  DRAFT_SECTION_SENTINEL,
  isSectionEndHtml,
  replaceEmptySectionHeadingLines,
  SECTION_END_MARKER,
  stripEmptySectionHeadingLines,
  stripEmptySectionHeadingMarkerLines,
} from '@heidi/editor/core';
import { sanitizeError } from '@heidi/runtime/logger';
import Showdown from 'showdown';

const disableListsExtension = () => {
  return [
    {
      type: 'lang',
      regex: /^( *)([*+-]|\d+\.)\s+/gm,
      replace: (match: string, spaces: string, bullet: string) => {
        if (/^\d+\.$/.test(bullet)) {
          return '\u2002'.repeat(spaces.length) + bullet.slice(0, -1) + '\\' + '.' + ' ';
        } else {
          return '\u2002'.repeat(spaces.length) + '\\' + bullet + ' ';
        }
      },
    },
  ];
};
export const cleanListItems = (html: string): string => {
  return html.replace(
    /<li>([\s\S]*?)<\/li>/g,
    (fullMatch: string, innerContent: string) => {
      // Remove the first <p>...</p> pair if it appears at the start of the <li> content.
      const cleanedContent = innerContent.replace(/^\s*<p>([\s\S]*?)<\/p>/, '$1');
      return `<li>${cleanedContent}</li>`;
    }
  );
};

/**
 * Clipboard stand-in for a collapsed `</p><p>`. External paste targets
 * still see a line break; Heidi paste restores the original split via
 * `restoreParagraphsFromBreakLines`. A plain `<br>` cannot carry that
 * distinction — it would serialize as a markdown hard break (`  \n`).
 *
 * `class` and `data-*` are both set so a sanitizer that strips one still
 * leaves the other for the restore regex.
 *
 * The marker rides the clipboard, so an external paste target does receive
 * it — an inert attribute pair on a `<br>` that renders as the same line
 * break. Sinks that persist HTML into another system (an EHR note push) must
 * reduce it with `stripParagraphBreakMarkers` instead, so the marker is never
 * filed into a record nothing will ever restore.
 */
const HEIDI_PARAGRAPH_BREAK_MARKER = 'heidi-paragraph-break';

export const HEIDI_PARAGRAPH_BREAK_HTML = `<br class="${HEIDI_PARAGRAPH_BREAK_MARKER}" data-${HEIDI_PARAGRAPH_BREAK_MARKER}="true" />`;

// Built from the marker so a rename cannot leave the matcher behind. Whole
// token on both sides without lookbehind (older WebKit throws at parse time):
// `["'\s=]` covers class="…"/class='…'/unquoted class=, and `data-` covers
// the data-attribute name. A foreign prefix class like `x-heidi-paragraph-break`
// therefore does not match; the trailing lookahead still rejects the suffix
// form `heidi-paragraph-break-x`.
const PARAGRAPH_BREAK_BR = new RegExp(
  `<br\\b[^>]*(?:["'\\s=]|data-)${HEIDI_PARAGRAPH_BREAK_MARKER}(?![\\w-])[^>]*/?>`,
  'gi'
);

// Non-global twin: `.test` on a `g` regex is stateful across calls. Avoids
// lowercasing the whole payload on every paste just to look for the marker.
const PARAGRAPH_BREAK_BR_TEST = new RegExp(PARAGRAPH_BREAK_BR.source, 'i');

export const replaceParagraphsWithBreakLines = (html: string): string =>
  html.replace(/<\/p><p>/g, HEIDI_PARAGRAPH_BREAK_HTML);

/**
 * Reduce the marker to the plain `<br />` it stands in for.
 *
 * For sinks that persist HTML outside Heidi — an EHR note push writes into a
 * patient record — where nothing will ever restore the split and the marker
 * would be filed permanently into someone else's system.
 *
 * `<br />` is what the collapse emitted before the marker existed, so the output
 * matches that on the plain path. A `DOMParser` round-trip (the external-paste
 * decoration passes below) re-serializes a void element as `<br>`, so a
 * decorated payload differs by the slash — same element, same parsed DOM.
 */
export const stripParagraphBreakMarkers = (html: string): string =>
  PARAGRAPH_BREAK_BR_TEST.test(html) ? html.replace(PARAGRAPH_BREAK_BR, '<br />') : html;

// Split keeps `<li>` / `<p>` tags at odd indices for the depth walk.
const BLOCK_BOUNDARY = /(<li\b[^>]*>|<\/li>|<p\b[^>]*>|<\/p>)/i;

/** Inverse of `replaceParagraphsWithBreakLines`. No-op when the marker is absent. */
export const restoreParagraphsFromBreakLines = (html: string): string => {
  if (!PARAGRAPH_BREAK_BR_TEST.test(html)) return html;
  // `reformatHtml` collapses `</p><p>` *then* `cleanListItems` unwraps the
  // leading `<p>` in each bare `<li>`, so a marked break inside a list item is
  // no longer between paragraphs. Restoring it to `</p><p>` would emit
  // `<li>a</p><p>b</li>` (a spurious empty paragraph). Demote those to `<br>`,
  // tracking `<li>` nesting depth — a lazy `<li>…</li>` pair-match would stop
  // at a nested item's close and wrongly restore a marker sitting after that
  // sublist in the same item.
  //
  // Deliberately broader than that unwrap: this also demotes a marker still
  // inside a well-formed `<p>` nested under an `<li>`, which could have been
  // restored. That costs fidelity in a nested list and never correctness, and
  // matches the pre-marker behaviour of collapsing every `</p><p>` outright.
  // Restore `</p><p>` only inside a `<p>` and outside `<li>`. Elsewhere demote
  // to `<br>` so a heading/td/bare marker does not emit unbalanced tags.
  const parts = html.split(BLOCK_BOUNDARY);
  let restored = '';
  let listDepth = 0;
  let pDepth = 0;
  parts.forEach((part, index) => {
    if (index % 2 === 1) {
      const closing = part.charAt(1) === '/';
      if (/^<\/?li/i.test(part)) {
        listDepth = closing ? Math.max(0, listDepth - 1) : listDepth + 1;
      } else {
        pDepth = closing ? Math.max(0, pDepth - 1) : pDepth + 1;
      }
      restored += part;
      return;
    }
    const split = listDepth === 0 && pDepth > 0;
    restored += part.replace(PARAGRAPH_BREAK_BR, split ? '</p><p>' : '<br />');
  });
  return restored;
};

/**
 * Inline border styles onto every `<table>` / `<th>` / `<td>` so pasted
 * tables render with borders in external targets (Word, Outlook, Apple
 * Mail) that don't fetch external stylesheets. Heidi's editor borders
 * live in CSS — they get stripped once the HTML fragment leaves the page.
 *
 * Pre-existing inline borders are preserved (only sets when absent).
 * SSR-safe: no-op when DOMParser is unavailable.
 */
export const decorateTablesForExternalPaste = (html: string): string => {
  if (!html || typeof DOMParser === 'undefined' || !html.includes('<table')) {
    return html;
  }
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const tables = doc.body.querySelectorAll('table');
  if (tables.length === 0) return html;

  tables.forEach((table) => {
    if (!table.style.border) table.style.border = '1px solid #000';
    if (!table.style.borderCollapse) table.style.borderCollapse = 'collapse';
    table.querySelectorAll('th, td').forEach((cell) => {
      const el = cell as HTMLElement;
      if (!el.style.border) el.style.border = '1px solid #000';
      if (!el.style.padding) el.style.padding = '4px 8px';
    });
  });

  return doc.body.innerHTML;
};

/**
 * Sibling of `decorateTablesForExternalPaste` for prose structure. External
 * EHR editors (Epic, Best Practice, Gentu) apply their own defaults to a bare
 * pasted fragment, producing inconsistent bold, spacing, and font bleed. This
 * pass:
 *   - sets `margin:0` on every `<p>` (belt-and-braces on top of the
 *     `replaceParagraphsWithBreakLines` collapse) so paste targets don't add
 *     inter-paragraph spacing;
 *   - rewrites `<strong>`→`<b>` and `<em>`→`<i>` (attributes/children
 *     preserved) — older RTF/Delphi-based rich-text controls parse `b`/`i` far
 *     more reliably than `strong`/`em`.
 *
 * Deliberately sets NO `font-family` / `font-size`: inlining a font causes
 * font bleed in the paste target, whereas omitting it lets the EHR apply its
 * own default. Do not add font declarations here.
 *
 * Pre-existing paragraph margins are preserved (only sets when absent).
 * SSR-safe: no-op when DOMParser is unavailable.
 */
export const decorateStructureForExternalPaste = (html: string): string => {
  if (!html || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

  doc.body.querySelectorAll('p').forEach((p) => {
    const el = p as HTMLElement;
    if (!(el.getAttribute('style') ?? '').includes('margin')) el.style.margin = '0';
  });

  doc.body.querySelectorAll('strong').forEach((strong) => {
    const b = doc.createElement('b');
    Array.from(strong.attributes).forEach((attr) =>
      b.setAttribute(attr.name, attr.value)
    );
    while (strong.firstChild) b.appendChild(strong.firstChild);
    strong.replaceWith(b);
  });

  doc.body.querySelectorAll('em').forEach((em) => {
    const i = doc.createElement('i');
    Array.from(em.attributes).forEach((attr) => i.setAttribute(attr.name, attr.value));
    while (em.firstChild) i.appendChild(em.firstChild);
    em.replaceWith(i);
  });

  return doc.body.innerHTML;
};

export const reformatHtml = (
  html: string,
  options?: { disableHtmlParagraphsWithBreakLines?: boolean }
): string => {
  if (!options?.disableHtmlParagraphsWithBreakLines) {
    html = replaceParagraphsWithBreakLines(html);
  }
  const htmlCleanedLists = cleanListItems(html);
  const decorated = decorateTablesForExternalPaste(htmlCleanedLists);
  if (!isCopyExternalPasteStylesEnabled()) return decorated;
  return decorateStructureForExternalPaste(decorated);
};

const EMPTY_PLACEHOLDER = 'ABAEMPTYLINEABA';
const CODE_BLOCK_SENTINEL = 'ABACODEBLOCKABA';
const HTML_TAG_SENTINEL = 'ABAHTMLTAGABA';

// Upper bound on the empty paragraphs one blank-line run can reconstruct. A
// human types single digits; a four-figure run only reaches storage from pasted
// or machine-generated content, where materialising one ProseMirror node per
// newline is pure cost. Above the cap the round trip stops being a fixed point
// (it settles at the cap on the next save) — an acceptable trade for content
// that is already degenerate.
const MAX_RECONSTRUCTED_BLANK_LINES = 50;

// Inline-formatting tags that markdown can't express, so they live as raw HTML
// in stored notes and must reach the editor intact to render. Every other tag —
// block/structural (`<h1>`, `<p>`, `<table>`, …), markdown-expressible
// (`<strong>`, `<em>`), and unknown — is treated as literal text the user typed
// and escaped, so e.g. a typed `<h1>hello</h1>` shows verbatim rather than
// becoming a heading.
const ROUND_TRIP_HTML_TAGS = new Set(['br', 'u', 's', 'del', 'sub', 'sup']);

// Every comment the system reserves for itself. One source shared by the
// hiding pass and the structural checks below, so a marker added later is
// covered everywhere without a second list to keep in sync.
const RESERVED_SYSTEM_COMMENT_SOURCE = `<!--\\s*(?:ehr-link:[\\s\\S]*?|section-end\\s*|${TABLE_COLWIDTHS_NAMESPACE_SOURCE})-->`;

const RESERVED_SYSTEM_COMMENT_REGEX = new RegExp(RESERVED_SYSTEM_COMMENT_SOURCE, 'g');

// Non-global twin: `.test` on a `g` regex is stateful across calls.
const RESERVED_SYSTEM_COMMENT_TEST_REGEX = new RegExp(RESERVED_SYSTEM_COMMENT_SOURCE);

/**
 * True when `line` carries a reserved system comment. Such a line is
 * structural, never cell content — it must reach its own handler intact.
 */
const hasReservedSystemComment = (line: string): boolean =>
  RESERVED_SYSTEM_COMMENT_TEST_REGEX.test(line);

/**
 * Strip fenced code blocks out of `markdown` into opaque sentinels so the
 * surrounding regex pipeline (bold/italic/paragraph/list/table) doesn't
 * mangle their content. Pair with `restoreCodeBlocks`.
 */
type FenceOpening = {
  character: '`' | '~';
  length: number;
  isRoot: boolean;
  hasBlockquote: boolean;
  maxClosingPrefixLength: number;
  renderable: boolean;
};

const isContainerPrefix = (prefix: string): boolean => {
  const hasContainer =
    prefix.includes('>') || /(?:^|[ \t])(?:[-+*]|\d+[.)])[ \t]+/.test(prefix);
  return hasContainer && /^[ \t>+*\-\d.)]*$/.test(prefix);
};

const findFenceOpening = (line: string): FenceOpening | undefined => {
  const match = /(`{3,}|~{3,})([^\r\n]*)\r?$/.exec(line);
  const marker = match?.[1];
  if (!match || !marker) return undefined;

  const prefix = line.slice(0, match.index);
  const isRoot = /^ {0,3}$/.test(prefix);
  if (!isRoot && !isContainerPrefix(prefix)) return undefined;

  const character = marker[0] as '`' | '~';
  const info = match[2] ?? '';
  if (character === '`' && info.includes('`')) return undefined;

  return {
    character,
    length: marker.length,
    isRoot,
    hasBlockquote: prefix.includes('>'),
    maxClosingPrefixLength: prefix.length + 3,
    renderable: isRoot && character === '`' && marker.length === 3,
  };
};

const isFenceClosing = (line: string, opening: FenceOpening): boolean => {
  const match = /(`{3,}|~{3,})[ \t]*\r?$/.exec(line);
  const marker = match?.[1];
  if (!match || !marker) return false;
  if (marker[0] !== opening.character || marker.length < opening.length) return false;

  const prefix = line.slice(0, match.index);
  if (opening.isRoot) return /^ {0,3}$/.test(prefix);
  if (opening.hasBlockquote && !prefix.includes('>')) return false;

  const validPrefix = /^[ \t]*$/.test(prefix) || isContainerPrefix(prefix);
  return validPrefix && prefix.length <= opening.maxClosingPrefixLength;
};

function extractCodeBlocks(markdown: string): {
  markdown: string;
  blocks: string[];
} {
  const renderCodeBlocks = isScribeBlockEditorCodeblockEnabled();
  const lines = markdown.split('\n');
  const stripped: string[] = [];
  const blocks: string[] = [];

  for (let index = 0; index < lines.length; index++) {
    const opening = findFenceOpening(lines[index] ?? '');
    if (!opening) {
      stripped.push(lines[index] ?? '');
      continue;
    }

    let closingIndex = index + 1;
    while (
      closingIndex < lines.length &&
      !isFenceClosing(lines[closingIndex] ?? '', opening)
    ) {
      closingIndex++;
    }
    const hasClosingFence = closingIndex < lines.length;
    const endIndex = hasClosingFence ? closingIndex : lines.length - 1;
    const raw = lines.slice(index, endIndex + 1).join('\n');

    let block = raw;
    if (renderCodeBlocks && opening.renderable && hasClosingFence) {
      const code = lines
        .slice(index + 1, closingIndex)
        .join('\n')
        .replace(/\r$/, '');
      const escaped = code
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      block = `<pre><code>${escaped}</code></pre>`;
    }

    const blockIndex = blocks.length;
    blocks.push(block);
    stripped.push(`${CODE_BLOCK_SENTINEL}${blockIndex}${CODE_BLOCK_SENTINEL}`);
    index = endIndex;
  }

  return { markdown: stripped.join('\n'), blocks };
}

function restoreCodeBlocks(html: string, blocks: string[]): string {
  if (blocks.length === 0) return html;
  return html.replace(
    new RegExp(
      `(?:<p>)?${CODE_BLOCK_SENTINEL}(\\d+)${CODE_BLOCK_SENTINEL}(?:</p>)?`,
      'g'
    ),
    (_, idx: string) => blocks[Number(idx)] ?? ''
  );
}

/**
 * Escape angle brackets that don't form a known editor tag, so literal text
 * such as `<content>` survives the markdown → HTML → editor round-trip. Left
 * raw, the browser/ProseMirror parse `<content>` as an unknown element and drop
 * it on the next render, silently deleting the user's text. Known formatting
 * tags (`<br>`, `<u>`, `<sub>`, …) are preserved untouched. A user-typed
 * comment like `<!-- note -->` is literal text, so it IS escaped (and shown).
 * Reserved system comments (`<!-- ehr-link:… -->` and `<!-- section-end -->`)
 * stay hidden.
 */
function escapeNonEditorHtml(markdown: string): string {
  const preserved: string[] = [];
  const stash = (value: string): string => {
    preserved.push(value);
    return `${HTML_TAG_SENTINEL}${preserved.length - 1}${HTML_TAG_SENTINEL}`;
  };

  // Stash system markers so they stay hidden comments (the section-close
  // marker, the legacy ehr-link marker, and anything in the colwidths
  // namespace — malformed variants included, so LLM-mangled markers never
  // surface as text); every other `<!-- … -->` is user-typed text and falls
  // through to be escaped.
  let out = markdown.replace(RESERVED_SYSTEM_COMMENT_REGEX, (match) => stash(match));

  // Stash known tags as opaque sentinels; everything else (unknown tags and
  // lone `<`/`>`) falls through to the blanket escape below.
  out = out.replace(
    /<\/?([a-zA-Z][a-zA-Z0-9]*)(?:\s[^<>]*)?\/?>/g,
    (match, tagName: string) =>
      ROUND_TRIP_HTML_TAGS.has(tagName.toLowerCase()) ? stash(match) : match
  );
  out = out.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  if (preserved.length > 0) {
    out = out.replace(
      new RegExp(`${HTML_TAG_SENTINEL}(\\d+)${HTML_TAG_SENTINEL}`, 'g'),
      (_match, idx: string) => preserved[Number(idx)] ?? ''
    );
  }
  return out;
}

function preprocessMarkdown(markdown: string): string {
  // Process empty lines and maintain paragraph structure
  return markdown
    .split('\n')
    .map((line) => (line.trim() === '' ? EMPTY_PLACEHOLDER : line))
    .join('\n')
    .replace(/\n/g, '\n\n'); // Force paragraph mode
}

function postprocessHtml(html: string): string {
  // Replace placeholder paragraphs and clean structure
  return html
    .replace(new RegExp(`<p>${EMPTY_PLACEHOLDER}</p>`, 'g'), '<p></p>')
    .replace(/\n/g, '');
}

export const markdownToHtml = ({
  markdown,
  showdownOptions: options,
  useSimpleMarkdownToHtml = false,
}: {
  markdown: string;
  showdownOptions?: Showdown.ConverterOptions & { disableLists?: boolean };
  useSimpleMarkdownToHtml?: boolean;
}) => {
  if (useSimpleMarkdownToHtml) {
    return simpleMarkdownToHtml({ markdown });
  }
  try {
    // Merge default options and passed-in options.
    // Remove the disableLists property so it doesn't interfere with Showdown.
    const { disableLists, extensions, ...restOptions } = options || {};
    const converterOptions: Showdown.ConverterOptions = {
      simpleLineBreaks: true,
      ...restOptions,
      extensions: extensions || [],
    };

    // If disableLists flag is true, add our custom extension.
    if (disableLists) {
      converterOptions.extensions = (
        Array.isArray(converterOptions.extensions) ? converterOptions.extensions : []
      ).concat(disableListsExtension());
    }

    const converter = new Showdown.Converter(converterOptions);
    const preprocessed = preprocessMarkdown(markdown);
    const html = converter.makeHtml(preprocessed);
    return postprocessHtml(html);
  } catch (error) {
    // Counter first: it must increment even if the logger itself throws.
    recordSerializerFallback();
    logger.error('[MD] serializer fell back to input', {
      serializer: 'markdownToHtml',
      error: sanitizeError(error),
      error_name: describeErrorName(error),
      input_length: markdown?.length ?? 0,
    });
    return markdown; // Fallback to original content
  }
};

interface ConversionOptions {
  bold?: boolean;
  italic?: boolean;
  paragraphs?: boolean;
  horizontalRule?: boolean;
  lists?: boolean;
  /**
   * When `true`, escape angle-bracket tags outside the round-trip allowlist to
   * literal text so typed `<content>` survives the note round-trip. Opt-in
   * (defaults to `false`): trusted-HTML callers such as the Ask Heidi / Evidence
   * inserts leave it off so their embedded HTML renders instead of being shown
   * as raw tags. Note / print / export round-trips pass `true`.
   */
  escapeUnknownHtml?: boolean;
  /**
   * When `true`, wrap `# ` heading blocks into section nodes. Explicit and
   * off by default: only section editors (those whose `useBlockEditorMarkdown`
   * sets `enableSections`) pass this, from the same reactive
   * `useSectionsWrappingEnabled()` derivation the editor uses, so the converter
   * and the editor can never disagree. Non-section editors omit it and their
   * `# ` headings round-trip untouched.
   */
  wrapSections?: boolean;
}

/**
 * Parses a markdown pipe-table line into trimmed cell values.
 * Returns null if the line doesn't look like a table row.
 */
function parseTableRow(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return null;
  // Split on pipes not preceded by a backslash so an escaped `\|` stays inside a
  // cell, drop the first/last empty entries from the leading/trailing pipes, then
  // unescape `\|` back to a literal `|` in each cell.
  const segments = trimmed.split(/(?<!\\)\|/);
  // A trailing `\|` satisfies endsWith('|') but is escaped content, not a
  // closing delimiter — the last segment then carries text and slicing it off
  // would silently drop it. Such a line is not a table row.
  if (segments[segments.length - 1] !== '') return null;
  const cells = segments.slice(1, -1);
  return cells.map((c) => c.trim().replace(/\\\|/g, '|'));
}

// Intentionally narrower than the save-side shield in `serializeTableCell`,
// which encodes every form the lists pass (`<ol[^>]*>`) would rewrite.
// Restoring turns stored text back into live markup, so only `start` is
// allowed — widening it is an attribute-injection surface, not a fix. Cost of
// the asymmetry: a list tag with any other attribute renders as literal text
// (no editor kit emits one; TipTap emits bare <ul>/<li> and <ol start>).
const ESCAPED_CELL_LIST_TAG = /&lt;(\/?(?:ol|ul|li)|ol\s+start="\d+")&gt;/g;

/**
 * Restore list tags inside a pipe-table cell that `escapeNonEditorHtml`
 * escaped earlier in the pipeline. The vMarkdown save serializer stores
 * multi-block cell content as inline <ol>/<ul>/<li> HTML inside GFM pipe
 * cells (the format has no block syntax within a cell), so inside a cell
 * these tags are the serializer's own output and must render as lists.
 * Outside tables the escape stands: typed list tags remain literal text.
 */
const restoreCellListTags = (cell: string, restore: boolean): string =>
  restore ? cell.replace(ESCAPED_CELL_LIST_TAG, '<$1>') : cell;

/**
 * Checks if a line is a markdown table separator row (e.g. |---|---|---|).
 */
function isTableSeparator(line: string): boolean {
  const cells = parseTableRow(line);
  if (!cells || cells.length === 0) return false;
  return cells.every((c) => /^[:\- ]+$/.test(c));
}

/**
 * `colwidth` attribute for a first-row cell, from a parsed colwidths marker.
 * TipTap's table extension parses this attribute natively and derives the
 * rendered colgroup from the table's first row, so stamping widths there is
 * all the editor needs to restore a resized column layout.
 */
function colwidthAttr(colwidths: number[] | undefined, index: number): string {
  const width = colwidths?.[index];
  return width ? ` colwidth="${width}"` : '';
}

/**
 * Converts consecutive markdown table lines into an HTML <table> string.
 * Expects at least a header row, separator row, and one body row.
 */
function convertMarkdownTableBlock(
  lines: string[],
  colwidths?: number[],
  restoreCellListHtml = false
): string {
  const firstLine = lines[0] ?? '';
  const headerCells = parseTableRow(firstLine);
  if (!headerCells) return lines.map((l) => `<p>${l}</p>`).join('');

  const thead = `<thead><tr>${headerCells
    .map(
      (c, index) =>
        `<th${colwidthAttr(colwidths, index)}><p>${restoreCellListTags(c, restoreCellListHtml)}</p></th>`
    )
    .join('')}</tr></thead>`;

  const bodyRows = lines.slice(2); // skip header + separator
  const tbodyRows = bodyRows
    .map((line) => {
      const cells = parseTableRow(line);
      if (!cells) return '';
      return `<tr>${cells.map((c) => `<td><p>${restoreCellListTags(c, restoreCellListHtml)}</p></td>`).join('')}</tr>`;
    })
    .filter(Boolean)
    .join('');

  const tbody = tbodyRows ? `<tbody>${tbodyRows}</tbody>` : '';
  return `<table>${thead}${tbody}</table>`;
}

/**
 * True when `lines` is a headerless pipe table: every line parses as a row
 * with ≥2 cells. Pipe-wrapped prose (e.g. `| just text |`) keeps rendering as
 * paragraphs, and because every row must qualify, a stray single-cell line
 * can't be absorbed into an adjacent table as a ragged row.
 */
function isHeaderlessTableRun(lines: string[]): boolean {
  // Break-glass: separator-less pipe rows fall back to paragraphs (legacy).
  if (isTableRoundTripFixKilled()) return false;
  if (lines.length === 0) return false;
  return lines.every((line) => {
    const cells = parseTableRow(line);
    return cells !== null && cells.length >= 2;
  });
}

/**
 * Collects the run of consecutive separator-less pipe rows starting at
 * `start`. Stops before a row whose next line is a separator — that row is the
 * header of an adjacent intact table and must not be swallowed into this run.
 */
function collectHeaderlessRun(lines: string[], start: number): string[] {
  const run: string[] = [];
  let i = start;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!parseTableRow(line) || isTableSeparator(line)) break;
    if (isTableSeparator(lines[i + 1] ?? '')) break;
    run.push(line);
    i++;
  }
  return run;
}

// How many continuation lines a split row may claim. Generation splits a cell
// once per list item, and medication and problem lists routinely run 15–30
// entries, so the window has to clear those with room to spare. The blank-line
// bail is what actually keeps prose out — a paragraph ends long before here —
// leaving this as a backstop so an unterminated pipe line can never scan the
// document.
const MAX_REJOIN_LOOKAHEAD = 40;

/**
 * Stitches a pipe row back together when it starts at `start` but its closing
 * `|` landed on a later line — generated notes emit real newlines inside a cell
 * where the save serializer would emit `<br>`. Returns the joined line and the
 * index it consumed through, or null when the run is not unambiguously a row.
 *
 * Bails (leaving the lines exactly as they are) on: a line that already closes
 * itself, a blank line, a line starting a new pipe row, an extracted code-block
 * sentinel, a `# ` heading, a reserved system comment, nothing closing within
 * the lookahead, or a joined result that isn't a ≥2-cell row. That last check
 * is what keeps prose out: a paragraph merely opening with `|` yields a single
 * cell. Crossing a system comment would fold the marker into a cell, where a
 * section-close marker stops closing its section and renders as text.
 *
 * The self-closing bail does double duty: a joined row ends with `|`, so a
 * rejoin is a fixed point, and a complete table's last row can never absorb
 * the paragraph after it.
 *
 * Closing is decided by `endsWithUnescapedPipe`, mirroring `parseTableRow`'s
 * escape rule: a line ending in `\|` is carrying a literal pipe in cell
 * content, so it neither marks the first line as already closed nor ends the
 * scan — the real closing line may still be ahead.
 */
const endsWithUnescapedPipe = (line: string): boolean => /(?<!\\)\|$/.test(line);

function rejoinRowAt(
  lines: string[],
  start: number
): { line: string; end: number } | null {
  const first = lines[start] ?? '';
  const trimmedFirst = first.trim();
  if (!trimmedFirst.startsWith('|') || endsWithUnescapedPipe(trimmedFirst)) return null;

  const limit = Math.min(start + MAX_REJOIN_LOOKAHEAD, lines.length - 1);
  const parts = [first.trimEnd()];
  for (let i = start + 1; i <= limit; i++) {
    const raw = lines[i] ?? '';
    const trimmed = raw.trim();
    if (
      trimmed === '' ||
      trimmed.startsWith('|') ||
      getCodeBlockSentinel(raw) !== null ||
      findSectionHeadingStart(raw, 0) !== -1 ||
      hasReservedSystemComment(raw)
    ) {
      return null;
    }
    parts.push(trimmed);
    if (!endsWithUnescapedPipe(trimmed)) continue;

    const line = parts.join('<br>');
    const cells = parseTableRow(line);
    return cells && cells.length >= 2 ? { line, end: i } : null;
  }
  return null;
}

/**
 * Rejoins every split pipe row in `lines` before any table scan reads it, so
 * both scan paths see the same normalized rows. Joining with `<br>` matches
 * what `serializeTableCell` writes back, which makes the row a round-trip fixed
 * point: it re-serializes to one line and is never a rejoin candidate again.
 *
 * `enabled` is resolved once per conversion by the caller — the lists path
 * calls this per content block, and a note hydrates on every load and save.
 */
function rejoinSplitTableRows(lines: string[], enabled: boolean): string[] {
  if (!enabled) return lines;
  const result: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const rejoined = rejoinRowAt(lines, i);
    if (rejoined) {
      result.push(rejoined.line);
      i = rejoined.end;
    } else {
      result.push(lines[i] ?? '');
    }
  }
  return result;
}

/**
 * Converts a run of headerless pipe-table lines into an HTML <table> with a
 * <tbody> only — every row's cells become <td>, no <thead>. Used when a table's
 * header row was deleted so no `|---|` separator was emitted on save.
 */
function convertHeaderlessTableBlock(
  lines: string[],
  colwidths?: number[],
  restoreCellListHtml = false
): string {
  const tbodyRows = lines
    .map((line, rowIndex) => {
      const cells = parseTableRow(line);
      if (!cells) return '';
      // Widths live on the first row only — TipTap derives the colgroup from it.
      const widths = rowIndex === 0 ? colwidths : undefined;
      return `<tr>${cells
        .map(
          (c, index) =>
            `<td${colwidthAttr(widths, index)}><p>${restoreCellListTags(c, restoreCellListHtml)}</p></td>`
        )
        .join('')}</tr>`;
    })
    .filter(Boolean)
    .join('');
  return `<table><tbody>${tbodyRows}</tbody></table>`;
}

/**
 * Processes markdown text, extracting table blocks and converting them to HTML.
 * Returns the text with table blocks replaced by their HTML equivalents.
 * Non-table lines are returned with a special marker so paragraph wrapping
 * can be applied only to them.
 */

// MDNE serialises an empty paragraph as a standalone `&nbsp;` line so CommonMark
// keeps the blank. Treat that line as one empty <p> and do not also promote the
// `\n\n` around it (the marker already owns the 2-newline separator).
const MDNE_EMPTY_PARAGRAPH_LINE = '&nbsp;';

const isMdneEmptyParagraphLine = (line: string): boolean =>
  line.trim() === MDNE_EMPTY_PARAGRAPH_LINE;

const lineStartingAt = (text: string, from: number): string => {
  const end = text.indexOf('\n', from);
  return text.slice(from, end === -1 ? text.length : end);
};

const lineEndingAt = (text: string, end: number): string => {
  if (end <= 0) return '';
  const prevNl = text.lastIndexOf('\n', end - 1);
  const start = prevNl === -1 ? 0 : prevNl + 1;
  return text.slice(start, end);
};

function extractAndConvertTables(
  markdown: string,
  restoreCellListHtml = false,
  rejoinSplitRows = false
): string {
  const lines = rejoinSplitTableRows(markdown.split('\n'), rejoinSplitRows);
  const result: string[] = [];
  let i = 0;
  // Widths from a colwidths marker line, applied to the table that starts on
  // the very next line. A marker not followed by a table is dropped silently —
  // it must never render as text.
  let pendingColwidths: number[] | undefined;

  while (i < lines.length) {
    const currentLine = lines[i] ?? '';
    const nextLine = lines[i + 1] ?? '';

    const markerWidths = parseTableColwidthsMarker(currentLine);
    if (markerWidths) {
      // Flag OFF: the marker line is still consumed (never rendered) but the
      // widths are not stamped — the single remote off-switch for the feature.
      pendingColwidths = isTableColwidthPersistenceEnabled() ? markerWidths : undefined;
      i++;
      continue;
    }
    if (isTableColwidthsNamespaceLine(currentLine)) {
      // Malformed namespace line (e.g. LLM-mangled): drop it, never render.
      i++;
      continue;
    }
    const colwidths = pendingColwidths;
    pendingColwidths = undefined;

    // Check if the current line could be the start of a table (header row)
    if (
      parseTableRow(currentLine) &&
      i + 1 < lines.length &&
      isTableSeparator(nextLine)
    ) {
      // Collect all consecutive table lines
      const tableLines: string[] = [currentLine, nextLine];
      i += 2;
      while (i < lines.length) {
        const bodyLine = lines[i] ?? '';
        if (parseTableRow(bodyLine) && !isTableSeparator(bodyLine)) {
          tableLines.push(bodyLine);
          i++;
        } else {
          break;
        }
      }
      result.push(convertMarkdownTableBlock(tableLines, colwidths, restoreCellListHtml));
    } else if (parseTableRow(currentLine) && !isTableSeparator(currentLine)) {
      // No separator followed the row — collect the run of consecutive rows and,
      // if it qualifies, treat it as a headerless table instead of paragraphs.
      const runLines = collectHeaderlessRun(lines, i);
      if (runLines.length === 0) {
        // Loop safety only — a header row followed by a separator is always
        // consumed by the branch above, so the run here is never empty.
        result.push(`<p>${currentLine}</p>`);
        i++;
        continue;
      }
      i += runLines.length;
      if (isHeaderlessTableRun(runLines)) {
        result.push(
          convertHeaderlessTableBlock(runLines, colwidths, restoreCellListHtml)
        );
      } else {
        result.push(...runLines.map((l) => `<p>${l}</p>`));
      }
    } else {
      if (colwidths) {
        logger.info('Dropped table colwidths marker not followed by a table', {
          serializer: 'simpleMarkdownToHtml',
          columnCount: colwidths.length,
        });
      }
      // Skip the CommonMark blank next to an MDNE empty-paragraph marker so
      // `a\n\n&nbsp;\n\nb` is one empty <p>, not three.
      if (
        currentLine.trim().length === 0 &&
        (isMdneEmptyParagraphLine(lines[i - 1] ?? '') ||
          isMdneEmptyParagraphLine(nextLine))
      ) {
        i++;
        continue;
      }
      result.push(
        isMdneEmptyParagraphLine(currentLine) ? '<p></p>' : `<p>${currentLine}</p>`
      );
      i++;
    }
  }

  return result.join('');
}

/**
 * Public entry for the vMDE markdown→HTML save serializer. Guards the
 * (previously unguarded) conversion pipeline with a named failure log and a
 * safe fallback to the original markdown, mirroring `markdownToHtml` above so a
 * throw is instrumented in Datadog instead of propagating uninstrumented.
 */
export function simpleMarkdownToHtml(args: {
  markdown: string;
  options?: ConversionOptions;
}): string {
  try {
    return runSimpleMarkdownToHtml(args);
  } catch (error) {
    recordSerializerFallback();
    logger.error('[MD] serializer fell back to input', {
      serializer: 'simpleMarkdownToHtml',
      error: sanitizeError(error),
      error_name: describeErrorName(error),
      input_length: args.markdown?.length ?? 0,
    });
    return args.markdown; // Fallback to original content
  }
}

/**
 * Converts markdown text to HTML with simple rules.
 * You can disable specific conversion rules by passing an options object.
 * Pass `options.lists=true` to opt into block-level list processing
 * (bullet/numbered/nested lists) via `simpleMarkdownToHtmlWithLists`.
 */
function runSimpleMarkdownToHtml({
  markdown,
  options = {},
}: {
  markdown: string;
  options?: ConversionOptions;
}): string {
  // Set defaults: if an option is NOT explicitly false, it'll be enabled.
  const opts = {
    bold: options.bold !== false,
    italic: options.italic !== false,
    paragraphs: options.paragraphs !== false,
    horizontalRule: options.horizontalRule !== false,
    lists: options.lists === true,
    escapeUnknownHtml: options.escapeUnknownHtml === true,
    wrapSections: options.wrapSections === true,
  };

  // Stash fenced code blocks before any regex pass: their content is opaque
  // (must not be parsed as bold/italic/lists/etc).
  const codeBlockExtraction = extractCodeBlocks(markdown);
  markdown = codeBlockExtraction.markdown;

  // Empty section headings need real content while inside ProseMirror, but the
  // stored comment must never render on non-editor surfaces. Only the canonical
  // marker is stripped here: non-section editors (context notes, dictation,
  // chat inserts) also hydrate through this path, and a literal `#` line there
  // is user content, not a section heading. Fenced code is already opaque here,
  // so a literal `#` inside a fence is unaffected.
  markdown = opts.wrapSections
    ? replaceEmptySectionHeadingLines(markdown, DRAFT_SECTION_SENTINEL)
    : stripEmptySectionHeadingMarkerLines(markdown);

  // remove span tags
  markdown = markdown.replace(/<\/?span[^>]*>/g, '');

  // Opt-in: escape literal angle brackets (e.g. typed `<content>`) before any
  // tag is emitted below, so they reach the editor as text instead of being
  // parsed as an unknown element and dropped on re-render. Off by default so
  // trusted-HTML callers (AI/Evidence inserts) keep their embedded HTML.
  if (opts.escapeUnknownHtml) {
    markdown = escapeNonEditorHtml(markdown);
  }

  // Process bold+italic (triple markers) before individual bold/italic
  if (opts.bold && opts.italic) {
    markdown = markdown.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    markdown = markdown.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');
  }

  // Process bold text (handle bold before italic to avoid conflicts)
  if (opts.bold) {
    markdown = markdown.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    markdown = markdown.replace(/__(.+?)__/g, '<strong>$1</strong>');
  }

  // Process italic text (after bold markers are consumed).
  // Negative lookbehind/lookahead ensures standalone * / _ are matched,
  // not leftover halves of unprocessed ** / __ when bold is disabled.
  if (opts.italic) {
    markdown = markdown.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    markdown = markdown.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');
  }

  // Cell list tags are only escaped when the escape pass ran, and the
  // break-glass reverts to leaving them escaped. Resolved once per conversion
  // rather than per cell — a table hydrates on every note load and save.
  const restoreCellListHtml = opts.escapeUnknownHtml && !isTableRoundTripFixKilled();

  // Both table switches gate the rejoin: the round-trip break-glass reverts
  // every table fix, so an incident responder gets the legacy parse from one
  // flip. Resolved once per conversion for the same reason as above — the lists
  // path rejoins per content block, so a per-call read is one provider hit per
  // section of the note.
  const rejoinSplitRows = !isTableRowRejoinKilled() && !isTableRoundTripFixKilled();

  if (opts.lists) {
    let listHtml = restoreCodeBlocks(
      simpleMarkdownToHtmlWithLists(markdown, {
        ...opts,
        restoreCellListHtml,
        rejoinSplitRows,
      }),
      codeBlockExtraction.blocks
    );
    // Wrap `# heading` blocks in sections, same as the non-lists path below —
    // otherwise enabling the lists option would silently drop section wrappers.
    if (opts.wrapSections) {
      listHtml = wrapHtmlInSections(listHtml);
    }
    return listHtml;
  }

  // Convert markdown tables to HTML and wrap remaining lines in <p> tags
  if (opts.paragraphs) {
    markdown = extractAndConvertTables(markdown, restoreCellListHtml, rejoinSplitRows);
    markdown = markdown.replace(/<br\s*\/?>/g, '</p><p>');
  } else {
    if (markdown.includes('\n\n')) {
      markdown = markdown
        .split('\n\n')
        .map((paragraph) => `<p>${paragraph}</p>`)
        .join('');
    }
    if (markdown.includes('\n')) {
      markdown = markdown.replace(/\n/g, '<br/>');
    }
  }

  // Replace --- with <hr>
  if (opts.horizontalRule) {
    markdown = markdown.replace(/<p>---<\/p>/g, '<hr>');
  }

  markdown = markdown.replace(/\n/g, '');
  markdown = markdown.replace(/&nbsp;/g, ' ');

  markdown = restoreCodeBlocks(markdown, codeBlockExtraction.blocks);

  if (opts.wrapSections) {
    markdown = wrapHtmlInSections(markdown);
  }

  return markdown;
}

// ── List-aware conversion (opt-in via options.lists=true) ──

function processGroup(
  lines: string[],
  type: 'text' | 'bullet' | 'numbered',
  opts: { paragraphs: boolean }
): string[] {
  if (type === 'bullet') {
    const items = lines
      .map((l) => l.replace(/^\s*[-*]\s+/, '').trim())
      .filter((item) => item.length > 0)
      .map((content) => `<li>${content}</li>`)
      .join('');
    return [`<ul>${items}</ul>`];
  }

  if (type === 'numbered') {
    const items = lines
      .map((l) => l.replace(/^\s*\d+\.\s+/, '').trim())
      .filter((item) => item.length > 0)
      .map((content) => `<li>${content}</li>`)
      .join('');
    return [`<ol>${items}</ol>`];
  }

  if (opts.paragraphs) {
    if (lines.length > 1) {
      const content = lines.join('<br/>');
      return [`<p>${content}</p>`];
    } else {
      return [`<p>${lines[0]}</p>`];
    }
  } else {
    const content = lines.join('<br/>');
    return [`<p>${content}</p>`];
  }
}

const findSectionHeadingStart = (text: string, offset: number): number => {
  let headingStart = offset;
  let spaces = 0;
  while (spaces < 3 && text[headingStart] === ' ') {
    headingStart++;
    spaces++;
  }
  return text.startsWith('# ', headingStart) ? headingStart : -1;
};

const CODE_BLOCK_SENTINEL_LINE = new RegExp(
  `^ {0,3}(${CODE_BLOCK_SENTINEL}\\d+${CODE_BLOCK_SENTINEL})[\\t ]*\\r?$`
);

const getCodeBlockSentinel = (line: string): string | null =>
  CODE_BLOCK_SENTINEL_LINE.exec(line)?.[1] ?? null;

function simpleMarkdownToHtmlWithLists(
  markdown: string,
  opts: {
    paragraphs: boolean;
    horizontalRule: boolean;
    restoreCellListHtml: boolean;
    rejoinSplitRows: boolean;
  }
): string {
  markdown = markdown.replace(/(\d+\.\s+[^\n]+)\n\n([-*]\s+)/g, '$1\n$2');

  // Treat a whitespace-only line (a lone space/tab/nbsp, as legacy notes and
  // some editor serialisations store an "empty" line) as a true blank line, so
  // the `\n{2,}` run detection below counts it. Without this a `\n \n` breaks the
  // newline run and the blank line is silently dropped on reload.
  markdown = markdown.replace(/^[^\S\n]+$/gm, '');

  // Collapse blank lines between consecutive same-type list items so a stray
  // blank doesn't split one list into two (which leaks a <p></p> that compounds).
  markdown = collapseSameTypeListItemGaps(markdown);

  const BLANK_LINE_MARKER = 'ABAEMPTYLINEABA';
  // Rebuild one empty-paragraph marker per blank line the user typed, as the
  // exact inverse of the serialiser, so the round-trip is a fixed point at every
  // count — empty paragraphs neither collapse nor multiply on save/reload:
  //   • content→content: k empty paragraphs ⟷ (k+1) newlines, so a run of N maps
  //     back to (N-1) markers (one blank line each).
  //   • content→`# ` heading: the heading owns a 2-newline standard separator, so
  //     k empty paragraphs ⟷ (k+2) newlines — a run of N before a heading maps
  //     back to max(0, N-2) markers (a lone blank line is the separator, not an
  //     empty paragraph). Detected by lookahead on the text after the run.
  markdown = markdown.replace(/\n{2,}/g, (run: string, offset: number, full: string) => {
    // Positional startsWith, not full.slice(...) + regex — this pass runs on
    // every note load/save, so peek in place instead of allocating the tail
    // per match (keeps the hydration pass linear rather than O(n^2)).
    const beforeHeading = findSectionHeadingStart(full, offset + run.length) !== -1;
    const alreadyNormalized =
      isMdneEmptyParagraphLine(lineStartingAt(full, offset + run.length)) ||
      isMdneEmptyParagraphLine(lineEndingAt(full, offset));
    const emptyParagraphs =
      beforeHeading || alreadyNormalized ? Math.max(0, run.length - 2) : run.length - 1;
    const markers = `${BLANK_LINE_MARKER}\n\n`.repeat(
      Math.min(emptyParagraphs, MAX_RECONSTRUCTED_BLANK_LINES)
    );
    return `\n\n${markers}`;
  });

  const blocks = markdown.split(/\n{2,}/);
  const htmlBlocks: string[] = [];

  for (const block of blocks) {
    // Preserve indentation on the first content line so the section-heading
    // grammar can distinguish 0–3 leading spaces from a four-space code indent
    // or a tab. Only the empty/sentinel checks need the fully trimmed value.
    const contentBlock = block.replace(/^[\r\n]+/, '').trimEnd();
    const trimmedBlock = contentBlock.trim();
    if (!trimmedBlock) continue;

    if (trimmedBlock === BLANK_LINE_MARKER || isMdneEmptyParagraphLine(trimmedBlock)) {
      htmlBlocks.push('<p></p>');
      continue;
    }

    // Rejoin before the list/table classification below reads the lines: the
    // tail of a split row looks like a numbered-list item until it is joined.
    const lines = rejoinSplitTableRows(contentBlock.split('\n'), opts.rejoinSplitRows);

    // A table at the start of a block is handled by the mid-block loop below
    // (it detects a header/separator pair at any index, including index 0, and
    // consumes only the contiguous table rows). Tables are intentionally NOT
    // special-cased here: routing a whole block through convertMarkdownTableBlock
    // dropped any trailing non-table line in the same block.
    const hasNumberedItems = lines.some((l) => /^\s*\d+\.\s+/.test(l));
    const hasBulletItems = lines.some((l) => /^\s*[-*]\s+/.test(l));
    const hasTextLines = lines.some(
      (l) => !/^\s*(\d+\.|[-*])\s+/.test(l.trim()) && l.trim().length > 0
    );

    if (hasNumberedItems && hasBulletItems && !hasTextLines) {
      const nestedHtml: string[] = [];
      let currentNumberedItem: {
        num: number;
        content: string;
        bullets: string[];
      } | null = null;

      for (const line of lines) {
        const numberedMatch = line.match(/^\s*(\d+)\.\s+(.+)$/);
        const bulletMatch = line.match(/^\s*[-*]\s+(.+)$/);

        if (numberedMatch) {
          if (currentNumberedItem) {
            const bulletsHtml =
              currentNumberedItem.bullets.length > 0
                ? `<ul>${currentNumberedItem.bullets.map((b) => `<li>${b}</li>`).join('')}</ul>`
                : '';
            nestedHtml.push(
              `<ol start="${currentNumberedItem.num}"><li>${currentNumberedItem.content}${bulletsHtml}</li></ol>`
            );
          }
          currentNumberedItem = {
            num: parseInt(numberedMatch[1]!, 10),
            content: numberedMatch[2]!.trim(),
            bullets: [],
          };
        } else if (bulletMatch && currentNumberedItem) {
          currentNumberedItem.bullets.push(bulletMatch[1]!.trim());
        }
      }

      if (currentNumberedItem) {
        const bulletsHtml =
          currentNumberedItem.bullets.length > 0
            ? `<ul>${currentNumberedItem.bullets.map((b) => `<li>${b}</li>`).join('')}</ul>`
            : '';
        nestedHtml.push(
          `<ol start="${currentNumberedItem.num}"><li>${currentNumberedItem.content}${bulletsHtml}</li></ol>`
        );
      }

      htmlBlocks.push(...nestedHtml);
      continue;
    }

    const isBulletList = lines.length > 0 && lines.every((l) => /^\s*[-*]\s+/.test(l));
    if (isBulletList) {
      const items = lines
        .map((l) => l.replace(/^\s*[-*]\s+/, '').trim())
        .filter((item) => item.length > 0)
        .map((content) => `<li>${content}</li>`)
        .join('');
      htmlBlocks.push(`<ul>${items}</ul>`);
      continue;
    }

    const isNumberedList = lines.length > 0 && lines.every((l) => /^\s*\d+\.\s+/.test(l));
    if (isNumberedList) {
      const items = lines
        .map((l) => l.replace(/^\s*\d+\.\s+/, '').trim())
        .filter((item) => item.length > 0)
        .map((content) => `<li>${content}</li>`)
        .join('');
      htmlBlocks.push(`<ol>${items}</ol>`);
      continue;
    }

    if (opts.horizontalRule && trimmedBlock === '---') {
      htmlBlocks.push('<hr>');
      continue;
    }

    const mixedContent: string[] = [];
    let currentGroup: string[] = [];
    let currentGroupType: 'text' | 'bullet' | 'numbered' | null = null;
    // End index (exclusive) of a pipe-row run already classified as NOT a
    // headerless table — its lines must flow through text grouping without the
    // tail re-entering the headerless branch alone.
    let headerlessRejectedUntil = 0;
    // Widths from a colwidths marker line, applied to the table that starts on
    // the very next line — mirroring the non-lists `extractAndConvertTables`
    // scan. A marker not followed by a table is dropped silently.
    let pendingColwidths: number[] | undefined;

    for (let li = 0; li < lines.length; li++) {
      const line = lines[li]!;

      const markerWidths = parseTableColwidthsMarker(line);
      if (markerWidths) {
        // Same flag gate as extractAndConvertTables: consume, don't stamp.
        pendingColwidths = isTableColwidthPersistenceEnabled() ? markerWidths : undefined;
        continue;
      }
      if (isTableColwidthsNamespaceLine(line)) {
        // Malformed namespace line: drop it, never render.
        continue;
      }
      const colwidths = pendingColwidths;
      pendingColwidths = undefined;

      // Extracted fenced code is an atomic block. It can share a single-newline
      // block with surrounding prose, and CommonMark permits up to three spaces
      // before the opening fence. Flush any open group and normalize that
      // indentation so restoreCodeBlocks replaces a top-level sentinel rather
      // than inserting <pre> inside a paragraph.
      const codeBlockSentinel = getCodeBlockSentinel(line);
      if (codeBlockSentinel !== null) {
        if (currentGroup.length > 0 && currentGroupType) {
          mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
        }
        mixedContent.push(codeBlockSentinel);
        currentGroup = [];
        currentGroupType = null;
        continue;
      }

      // A table can sit anywhere inside a block, not just at lines[0]/lines[1]
      // (the block-level check above). Generated notes put a table directly
      // under a `# ` section heading with no blank line between, so the table
      // header is mid-block — detect a header row followed by a separator here
      // too, mirroring the non-lists `extractAndConvertTables` scan.
      if (parseTableRow(line) && isTableSeparator(lines[li + 1] ?? '')) {
        if (currentGroup.length > 0 && currentGroupType) {
          mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
          currentGroup = [];
          currentGroupType = null;
        }
        const tableLines: string[] = [line, lines[li + 1]!];
        let next = li + 2;
        while (
          next < lines.length &&
          parseTableRow(lines[next]!) &&
          !isTableSeparator(lines[next]!)
        ) {
          tableLines.push(lines[next]!);
          next++;
        }
        mixedContent.push(
          convertMarkdownTableBlock(tableLines, colwidths, opts.restoreCellListHtml)
        );
        li = next - 1;
        continue;
      }

      // Headerless pipe table: a run of rows with no separator (e.g. the header
      // row was deleted, so save emitted no `|---|`). Recognise it here too,
      // mirroring extractAndConvertTables, instead of dropping to paragraphs.
      if (
        li >= headerlessRejectedUntil &&
        parseTableRow(line) &&
        !isTableSeparator(line)
      ) {
        const runLines = collectHeaderlessRun(lines, li);
        if (isHeaderlessTableRun(runLines)) {
          if (currentGroup.length > 0 && currentGroupType) {
            mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
            currentGroup = [];
            currentGroupType = null;
          }
          mixedContent.push(
            convertHeaderlessTableBlock(runLines, colwidths, opts.restoreCellListHtml)
          );
          li += runLines.length - 1;
          continue;
        }
        // The run failed to qualify (e.g. a ragged mix of 1- and 2-cell rows).
        // Classification is per-run: mark the whole run rejected so a
        // multi-cell tail can't qualify by itself on a later iteration —
        // extractAndConvertTables keeps such a run as text, and both paths
        // must agree.
        headerlessRejectedUntil = li + runLines.length;
      }

      // A section-close marker line is always its own top-level element — the
      // vMarkdown serializer separates paragraphs with single newlines, so the
      // marker usually arrives glued to adjacent lines in one block. Without
      // this, it is <br/>-joined into a paragraph where wrapHtmlInSections
      // cannot see it and the text after the section is re-absorbed on reload.
      if (isSectionEndHtml(line)) {
        if (currentGroup.length > 0 && currentGroupType) {
          mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
        }
        mixedContent.push(SECTION_END_MARKER);
        currentGroup = [];
        currentGroupType = null;
        continue;
      }

      // A single `# ` heading is always its own block — even with no blank line
      // before or after it — so wrapHtmlInSections recognises it as a section
      // boundary instead of <br/>-joining it into adjacent content (which would
      // pull the body into the heading, or bury the next header in the body).
      // CommonMark permits up to three leading spaces before an ATX heading.
      // Keep this grammar aligned with countMarkdownSections and the blank-run
      // lookahead above; tabs and four-space indents remain ordinary content.
      const headingStart = findSectionHeadingStart(line, 0);
      if (headingStart !== -1) {
        if (currentGroup.length > 0 && currentGroupType) {
          mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
        }
        mixedContent.push(`<p>${line.slice(headingStart).trimEnd()}</p>`);
        currentGroup = [];
        currentGroupType = null;
        continue;
      }

      const isBullet = /^\s*[-*]\s+/.test(line);
      const isNumbered = /^\s*\d+\.\s+/.test(line);

      if (isBullet || isNumbered) {
        const newType = isBullet ? 'bullet' : 'numbered';

        if (currentGroupType !== null && currentGroupType !== newType) {
          mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
          currentGroup = [line];
          currentGroupType = newType;
        } else {
          currentGroup.push(line);
          currentGroupType = newType;
        }
      } else {
        if (currentGroupType === 'text' || currentGroupType === null) {
          currentGroup.push(line);
          currentGroupType = 'text';
        } else {
          mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
          currentGroup = [line];
          currentGroupType = 'text';
        }
      }
    }

    if (currentGroup.length > 0 && currentGroupType) {
      mixedContent.push(...processGroup(currentGroup, currentGroupType, opts));
    }

    htmlBlocks.push(...mixedContent);
  }

  let result = htmlBlocks.join('');
  result = result.replace(/&nbsp;/g, ' ');

  return result;
}

export function simpleMarkdownToHtmlForPrint({
  markdown,
  options = {},
}: {
  markdown: string;
  options?: ConversionOptions;
}): string {
  // Stash fenced code blocks before any regex pass — same reason as
  // simpleMarkdownToHtml: their content must not be mangled by the bold/
  // italic/paragraph regexes below.
  const codeBlockExtraction = extractCodeBlocks(markdown);
  markdown = codeBlockExtraction.markdown;

  // Print/export is a presentation boundary: remove the complete placeholder
  // heading rather than exposing the persisted HTML comment or a stray `#`.
  markdown = stripEmptySectionHeadingLines(markdown);
  // Same boundary rule for the table colwidths marker — print output must not
  // carry the persisted comment.
  markdown = stripTableColwidthsMarkerLines(markdown);

  // 1) Clean spans
  markdown = markdown.replace(/<\/?span[^>]*>/g, '');

  // Opt-in (same as simpleMarkdownToHtml): escape literal angle brackets so
  // typed `<content>` survives print/export instead of being parsed as an
  // unknown element and dropped. The print/export callers pass `true`.
  if (options.escapeUnknownHtml === true) {
    markdown = escapeNonEditorHtml(markdown);
  }

  // 2) Horizontal rule lines (--- on its own line)
  if (options.horizontalRule ?? true) {
    markdown = markdown.replace(/^\s*---\s*$/gm, '<hr/>');
  }

  // 3) Split into blocks by 2+ newlines (paragraphs or lists)
  const blocks = markdown.split(/\n{2,}/);

  const htmlBlocks = blocks.map((rawBlock) => {
    const block = rawBlock.trim();
    if (!block) return ''; // skip empty

    // Bold & italic (do before splitting lines)
    let text = block;
    if (options.bold !== false && options.italic !== false) {
      text = text.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
      text = text.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');
    }
    if (options.bold !== false) {
      text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      text = text.replace(/__(.+?)__/g, '<strong>$1</strong>');
    }
    if (options.italic !== false) {
      text = text.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
      text = text.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');
    }

    // If block looks like a list, render a real list
    const lines = text.split('\n');
    const isList = lines.every((l) => /^\s*[-*]\s+/.test(l));

    if (isList) {
      const items = lines
        .map((l) => l.replace(/^\s*[-*]\s+/, '').trim())
        .map((content) => `<li>${content}</li>`)
        .join('');
      return `<ul class="list">${items}</ul>`;
    }

    // Otherwise, normal paragraph: single newlines -> <br>
    const inner = lines.join('<br/>');
    return `<p class="para">${inner}</p>`;
  });

  // 4) Join and normalize spaces (don’t remove <br/>)
  return restoreCodeBlocks(htmlBlocks.join(''), codeBlockExtraction.blocks);
}
